import { getModulePath, LEAGUE_MODULE_NAMES } from '@leagueplatform/core';
import HomeIcon from 'assets/nav/home.svg';
import BenefitsIcon from 'assets/nav/wallet.svg';
import GetCareIcon from 'assets/nav/get-care.svg';
import SupportIcon from 'assets/nav/support.svg';
import JourneyIcon from 'assets/nav/journey.svg';
import HomeSelectedIcon from 'assets/nav/home-selected.svg';
import BenefitsSelectedIcon from 'assets/nav/wallet-selected.svg';
import GetCareSelectedIcon from 'assets/nav/get-care-selected.svg';
import JourneySelectedIcon from 'assets/nav/journey-selected.svg';
import SupportSelectedIcon from 'assets/nav/support-selected.svg';
import { APP_MANAGED_ROUTES } from 'common/app-managed-routes';
import { useWalletItemsSectionedQuery } from 'api/get-wallet-items-sectioned';
import { useGetJourneyRewardsProgram } from 'hooks/use-get-journey-rewards-program.hook';
import { HEALTH_JOURNEY_ASSESSMENT_LINK } from 'common/constants';

export enum NavIds {
  HOME = 'HOME',
  BENEFITS = 'BENEFITS',
  CARE = 'GET_CARE',
  JOURNEY = 'JOURNEY',
  SUPPORT = 'SUPPORT',
  COVERAGE = 'COVERAGE',
  HEALTH_AND_WELLNESS = 'HEALTH_AND_WELLNESS',
  ADDITIONAL_RESOURCES = 'ADDITIONAL_RESOURCES',
  GET_CARE = 'GET_CARE',
  GET_HELP_NOW = 'GET_HELP_NOW',
  COMPLETE_YOUR_HEALTH_ASSESSMENT = 'COMPLETE_YOUR_HEALTH_ASSESSMENT',
  WELLNESS_REWARDS_PROGRAM = 'WELLNESS_REWARDS_PROGRAM',
  CONNECT_WITH_US = 'CONNECT_WITH_US',
  HEALTH_PLAN_DOCUMENTS = 'HEALTH_PLAN_DOCUMENTS',
  HOW_CAN_WE_HELP = 'HOW_CAN_WE_HELP_NAV_LINK',
}

export enum LinkType {
  NAV = 'navLink',
  LINK = 'link',
  SUB_MENU = 'subMenu',
}

export type NavItem = {
  to:
    | string
    | {
        search: string;
        pathname: string;
        state: unknown;
        hash: string;
        key?: string | undefined;
      };
};

export interface NavLink extends NavItem {
  message_id: NavIds;
  type: LinkType;
  icon?: string;
  iconSelected?: string;
  subMenu?: {
    items: NavLink[];
  };
  text?: string;
}
// enable is used to only enable to query if the navbar enhancements are enabled
const useBenefitsSubMenuNavLinks = (enableQuery = false) => {
  const { data: walletItemsSectioned, isLoading } =
    useWalletItemsSectionedQuery({ enabled: enableQuery });
  const benefitsEntryPath = getModulePath(LEAGUE_MODULE_NAMES.wallet);

  const subMenuItemsMap: Record<string, NavLink> = {
    coverage: {
      to: `${benefitsEntryPath}#coverage`,
      message_id: NavIds.COVERAGE,
      type: LinkType.LINK,
    },
    'health-and-wellness': {
      to: `${benefitsEntryPath}#health-and-wellness`,
      message_id: NavIds.HEALTH_AND_WELLNESS,
      type: LinkType.LINK,
    },
    'additional-resources': {
      to: `${benefitsEntryPath}#additional-resources`,
      message_id: NavIds.ADDITIONAL_RESOURCES,
      type: LinkType.LINK,
    },
  };

  const subMenuItems: NavLink[] = [];

  if (!isLoading && walletItemsSectioned) {
    const walletSections = walletItemsSectioned?.items_sectioned;

    if (walletSections) {
      // Get all the section ids from the wallet landing page sub-sections
      const subSectionIds = walletSections
        .map((section: any) =>
          section?.sections.map((subSection: any) => subSection.section_id),
        )
        .flat();

      Object.entries(subMenuItemsMap).forEach(([key, value]) => {
        if (subSectionIds.includes(key)) {
          subMenuItems.push(value);
        }
      });

      return { dynamicBenefitsSubMenuItems: subMenuItems };
    }
  }

  // If the wallet landing page sub-sections are not loaded, use the default submenu items
  Object.values(subMenuItemsMap).forEach((value) => {
    subMenuItems.push(value);
  });

  return { dynamicBenefitsSubMenuItems: subMenuItems };
};

const useJourneySubMenuNavLinks = () => {
  const { data: rewardsProgramData, isLoading } = useGetJourneyRewardsProgram();
  const journeyEntryPath = getModulePath(LEAGUE_MODULE_NAMES.healthJourney);
  const journeySubMenuLinks: NavLink[] = [
    {
      // Default submenu link, the Health Assessment link is the same across apps
      to: `${journeyEntryPath}${HEALTH_JOURNEY_ASSESSMENT_LINK}`,
      message_id: NavIds.COMPLETE_YOUR_HEALTH_ASSESSMENT,
      type: LinkType.LINK,
    },
  ];

  if (isLoading) return { dynamicJourneySubMenuLinks: journeySubMenuLinks };

  // If rewards program loads, try adding it to the Journey submenu
  if (rewardsProgramData?.attributes?.items?.length > 0) {
    const rewardsProgram = rewardsProgramData?.attributes?.items[0];

    const rewardsProgramLink = rewardsProgram?.attributes?.cta;
    const rewardsProgramHeading = rewardsProgram?.attributes?.heading;

    if (rewardsProgramLink && rewardsProgramHeading) {
      journeySubMenuLinks.push({
        to: rewardsProgramLink,
        message_id: rewardsProgramHeading,
        type: LinkType.LINK,
        text: rewardsProgramHeading,
      });
    }
  }

  return { dynamicJourneySubMenuLinks: journeySubMenuLinks };
};

// TODO [HMRK-15478] remove enableNavbarEnhancements once navigation enhancements are released.
export const useNavLinksList = (enableNavbarEnhancements?: boolean) => {
  const { dynamicBenefitsSubMenuItems } = useBenefitsSubMenuNavLinks(
    enableNavbarEnhancements,
  );
  const { dynamicJourneySubMenuLinks } = useJourneySubMenuNavLinks();
  const benefitsEntryPath = getModulePath(LEAGUE_MODULE_NAMES.wallet);
  const journeyEntryPath = getModulePath(LEAGUE_MODULE_NAMES.healthJourney);
  const careEntryPath = getModulePath(LEAGUE_MODULE_NAMES.careDiscovery);
  const supportEntryPath = APP_MANAGED_ROUTES.SUPPORT;

  const enhancedNavLinksList: NavLink[] = [
    {
      to: '/home',
      message_id: NavIds.HOME,
      type: LinkType.NAV,
      icon: HomeIcon,
      iconSelected: HomeSelectedIcon,
    },
    {
      to: benefitsEntryPath,
      message_id: NavIds.BENEFITS,
      type: LinkType.SUB_MENU,
      icon: BenefitsIcon,
      iconSelected: BenefitsSelectedIcon,
      subMenu: {
        items: dynamicBenefitsSubMenuItems,
      },
    },
    {
      to: careEntryPath,
      message_id: NavIds.CARE,
      type: LinkType.SUB_MENU,
      icon: GetCareIcon,
      iconSelected: GetCareSelectedIcon,
      subMenu: {
        items: [
          {
            to: `${careEntryPath}#how-can-we-help`,
            message_id: NavIds.HOW_CAN_WE_HELP,
            type: LinkType.LINK,
          },
          {
            to: `${careEntryPath}#get-help-now`,
            message_id: NavIds.GET_HELP_NOW,
            type: LinkType.LINK,
          },
        ],
      },
    },
    {
      to: journeyEntryPath,
      message_id: NavIds.JOURNEY,
      type: LinkType.SUB_MENU,
      icon: JourneyIcon,
      iconSelected: JourneySelectedIcon,
      subMenu: {
        items: dynamicJourneySubMenuLinks,
      },
    },
    {
      to: supportEntryPath,
      message_id: NavIds.SUPPORT,
      type: LinkType.SUB_MENU,
      icon: SupportIcon,
      iconSelected: SupportSelectedIcon,
      subMenu: {
        items: [
          {
            to: `${supportEntryPath}#connect-with-us`,
            message_id: NavIds.CONNECT_WITH_US,
            type: LinkType.LINK,
          },
          {
            to: `${supportEntryPath}#health-plan-documents`,
            message_id: NavIds.HEALTH_PLAN_DOCUMENTS,
            type: LinkType.LINK,
          },
        ],
      },
    },
  ];

  // TODO [HMRK-15478] remove legacyNavLinksList once navigation enhancements are released.
  const legacyNavLinksList: NavLink[] = [
    {
      to: '/home',
      message_id: NavIds.HOME,
      type: LinkType.NAV,
      icon: HomeIcon,
      iconSelected: HomeSelectedIcon,
    },
    {
      to: getModulePath(LEAGUE_MODULE_NAMES.wallet),
      message_id: NavIds.BENEFITS,
      type: LinkType.NAV,
      icon: BenefitsIcon,
      iconSelected: BenefitsSelectedIcon,
    },
    {
      to: getModulePath(LEAGUE_MODULE_NAMES.careDiscovery),
      message_id: NavIds.CARE,
      type: LinkType.NAV,
      icon: GetCareIcon,
      iconSelected: GetCareSelectedIcon,
    },
    {
      to: getModulePath(LEAGUE_MODULE_NAMES.healthJourney),
      message_id: NavIds.JOURNEY,
      type: LinkType.NAV,
      icon: JourneyIcon,
      iconSelected: JourneySelectedIcon,
    },
    {
      to: APP_MANAGED_ROUTES.SUPPORT,
      message_id: NavIds.SUPPORT,
      type: LinkType.NAV,
      icon: SupportIcon,
      iconSelected: SupportSelectedIcon,
    },
  ];

  return {
    navLinks: enableNavbarEnhancements
      ? enhancedNavLinksList
      : legacyNavLinksList,
  };
};
